import React from 'react';
import { CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { Tooltip } from '@mui/material';
import { TFunction } from 'i18next';
import { MRT_ColumnDef } from 'material-react-table';
import styles from '../styles.module.scss';
import { LoanInventoryTableFields } from '../types';
import {
  renderDefaultCell,
  renderLoanStatusCell,
  createUnderwritingApprovedlenderNameRenderer,
  renderCmhcAccountNumberCell,
  renderRemark
} from './loansInventoryTableCellRenderers';
import { createRenderRateWithPercentage } from '../../HbtMasterPoolTable/columns/masterPoolInventoryTableCellRenderers';
import { renderDataTestId } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementTable/vriTableColumns';

export const createLoansInventoryColumns = (
  t: TFunction,
  language: string
): MRT_ColumnDef<LoanInventoryTableFields>[] => {
  const underWritingLenderNameRenderer = createUnderwritingApprovedlenderNameRenderer(language);
  const cmhcAccountNumberCellRenderer = renderCmhcAccountNumberCell(t);
  const LoanRecordStatusCellRenderer = renderLoanStatusCell(t);
  const renderRateWithPercentage = createRenderRateWithPercentage(language, 4);

  return [
    {
      accessorKey: 'cmhcAccountNumber' as const,
      header: t('CMHC-Account-Number'),
      Cell: renderDefaultCell,
      enableSorting: true,
      size: language === 'en' ? 260 : 275,
      muiTableHeadCellProps: renderDataTestId('cmhcAccountNumber')
    },
    {
      accessorKey: 'lenderReferenceNumber' as const,
      header: t('Lender-Reference-Number'),
      Cell: renderDefaultCell,
      enableSorting: true,
      size: language === 'en' ? 275 : 315,
      muiTableHeadCellProps: renderDataTestId('lenderReferenceNumber')
    },
    {
      accessorKey: 'loanStatus' as const,
      header: t('Loan-Status'),
      Cell: LoanRecordStatusCellRenderer,
      enableSorting: true,
      size: language === 'en' ? 250 : 250,
      muiTableHeadCellProps: renderDataTestId('loanStatus')
    },
    {
      accessorKey: 'lowRatioCmhcAccountNumber' as const,
      header: t('CMHC-Low-Ratio-Account-Number'),
      Cell: cmhcAccountNumberCellRenderer,
      enableSorting: true,
      size: language === 'en' ? 330 : 330,
      muiTableHeadCellProps: renderDataTestId('lowRatioCmhcAccountNumber')
    },
    {
      accessorKey: 'propertyAddress' as const,
      header: t('Property-Address'),
      Header: ({ column }: any) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <div>{column.columnDef?.header}</div>
            <Tooltip
              title={t('Property-Address-Tool-Tip-Text')}
              placement="top-start"
              arrow
              slotProps={{
                tooltip: {
                  className: styles.loansInventoryToolTip
                },
                arrow: {
                  className: styles.tooltipArrow
                }
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  marginLeft: '8px'
                }}
              >
                <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
              </div>
            </Tooltip>
          </div>
        );
      },
      Cell: renderDefaultCell,
      enableSorting: false,
      size: language === 'en' ? 230 : 260,
      muiTableHeadCellProps: renderDataTestId('propertyAddress')
    },
    {
      accessorKey: 'underwritingApprovedLenderCode' as const,
      header: t('Underwriting-Approved-Lender-Code'),
      Cell: renderDefaultCell,
      enableSorting: true,
      size: language === 'en' ? 355 : 420,
      muiTableHeadCellProps: renderDataTestId('underwritingApprovedLenderCode')
    },
    {
      accessorKey: 'underwritingApprovedLenderName' as const,
      header: t('Underwriting-Approved-Lender-Name'),
      Cell: underWritingLenderNameRenderer,
      enableSorting: true,
      size: language === 'en' ? 355 : 420,
      muiTableHeadCellProps: renderDataTestId('underwritingApprovedLenderName')
    },
    {
      accessorKey: 'loanPremiumRate' as const,
      header: t('Loan-Premium-Rate'),
      Cell: renderRateWithPercentage,
      enableSorting: true,
      size: language === 'en' ? 250 : 270,
      Header: ({ column }: any) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <div>{column.columnDef?.header}</div>
            <Tooltip
              title={t('Loan-Premium-Rate-Tool-Tip')}
              placement="top-start"
              arrow
              slotProps={{
                tooltip: {
                  className: styles.loansInventoryToolTip
                },
                arrow: {
                  className: styles.tooltipArrow
                }
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  marginLeft: '8px'
                }}
              >
                <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
              </div>
            </Tooltip>
          </div>
        );
      },
      muiTableHeadCellProps: renderDataTestId('loanPremiumRate')
    },
    {
      accessorKey: 'latestRemark' as const,
      header: t('Remarks'),
      muiTableBodyCellProps(props) {
        return {
          'aria-label': language === 'en' ? `remarks ${props.row.id}` : `remarques ${props.row.id}`
        };
      },
      Cell: renderRemark,
      Header: ({ column }: any) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <div>{column.columnDef?.header}</div>
            <Tooltip
              title={t('Remarks-Tool-Tip')}
              placement="top-start"
              arrow
              slotProps={{
                tooltip: {
                  className: styles.loansInventoryToolTip
                },
                arrow: {
                  className: styles.tooltipArrow
                }
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  marginLeft: '8px'
                }}
              >
                <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
              </div>
            </Tooltip>
          </div>
        );
      },
      enableSorting: true,
      size: language === 'en' ? 170 : 190,
      muiTableHeadCellProps: renderDataTestId('latestRemark')
    }
  ];
};
